import { useState } from "react";
import { Link } from "react-router-dom";
import { Headphones, MapPin, Video } from "lucide-react";
import type { Article } from "@/lib/mock-data";
import { useI18n } from "@/lib/i18n";
import { CategoryTag } from "./Badges";
import { useCategories } from "@/hooks/useCategories";
import { usePays } from "@/hooks/usePays";
import type { CategorieDto } from "@/types/categorie.types";
import type { PaysDto } from "@/types/pays.types";

function fmtDate(iso: string, locale: string) {
  return new Date(iso).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ArticleCard({
  article,
  variant = "default",
}: {
  article: Article;
  variant?: "default" | "compact" | "wide" | "hero";
}) {
  const { locale, t } = useI18n();
  const { data: categoriesData } = useCategories();
  const categories: CategorieDto[] = Array.isArray(categoriesData) ? categoriesData : [];
  const { data: paysData } = usePays();
  const paysListe: PaysDto[] = Array.isArray(paysData) ? paysData : [];
  const [imageEnErreur, setImageEnErreur] = useState(false);

  const title = article.title[locale] ?? article.title.fr ?? "";
  const summary = article.summary?.[locale] ?? article.summary?.fr;
  const imageUrl = article.coverImageUrl ?? article.imageUrl;

  const articleCats = categories.filter((c) => article.categories?.includes(String(c.id)));
  const label = articleCats.length
    ? articleCats.map((c) => (locale === "fr" ? c.libelleFr : c.libelleEn)).join(", ")
    : undefined;

  const articlePays = paysListe.filter((p) => article.pays?.includes(String(p.id)));
  const paysLabel = articlePays.length
    ? articlePays.map((p) => (locale === "fr" ? p.nom : (p.nomEn || p.nom))).join(", ")
    : undefined;

  return (
    <article className={`group flex ${variant === "wide" ? "flex-row gap-5" : "flex-col gap-3"}`}>
      <Link
        to={`/article/${article.slug}`}
        className={`media-heavy block overflow-hidden rounded-md bg-muted ${
          variant === "wide"
            ? "h-32 w-44 shrink-0"
            : variant === "compact"
            ? "aspect-[16/10]"
            : variant === "hero"
            ? "h-48 sm:h-72 md:h-96 lg:h-[480px]"
            : "aspect-[16/9]"
        }`}
      >
        {imageUrl && !imageEnErreur ? (
          <img
            src={imageUrl}
            alt={title}
            onError={() => setImageEnErreur(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ) : article.videoUrl ? (
          // ✅ Thumbnail vidéo avec icône play
          <div className="relative h-full w-full bg-foreground/90">
            <video
              src={article.videoUrl}
              className="h-full w-full object-cover opacity-60"
              muted
              preload="metadata"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90">
                <Video className="h-4 w-4 text-foreground" />
              </div>
            </div>
          </div>
        ) : article.audioUrl ? (
          // ✅ Thumbnail audio
          <div className="relative h-full w-full bg-gradient-to-br from-amber-900/80 to-amber-600/40 flex items-center justify-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90">
              <Headphones className="h-4 w-4 text-foreground" />
            </div>
          </div>
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-foreground/85 via-foreground/70 to-verified/40 transition-transform duration-500 group-hover:scale-[1.02]" />
        )}
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {label && <CategoryTag>{label}</CategoryTag>}
          {article.audioUrl && <Headphones className="h-3 w-3 text-muted-foreground" />}
          {article.videoUrl && <Video className="h-3 w-3 text-muted-foreground" />}
        </div>
        {paysLabel && (
          <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
            <MapPin className="h-3 w-3" /> {paysLabel}
          </div>
        )}
        <Link to={`/article/${article.slug}`} className="mt-1.5 block">
          <h3 className={`font-semibold leading-snug tracking-tight text-foreground group-hover:text-verified transition-colors ${
            variant === "compact" ? "text-base" : variant === "wide" ? "text-lg" : variant === "hero" ? "text-xl md:text-2xl" : "text-lg md:text-xl"
          }`}>
            {title}
          </h3>
        </Link>
        {summary && variant !== "compact" && (
          <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{summary}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
          <span>
            {article.byline === "partner-desk" ? t("label.partnerDesk") : t("label.byDesk")}
          </span>
          <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/60" />
          <span>{fmtDate(article.publishedAt, locale)}</span>
        </div>
      </div>
    </article>
  );
}